import { useQuery, UseQueryResult } from "@tanstack/react-query";
import config from "../config.json";
import {
  ESmppBindType,
  ESmppInterfaceVersion,
  ESmppNumberingPlanIndicator,
  ESmppTypeOfNumber,
} from "../types/smpp/smpp.type";
import { IPageable, Option, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";

const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

enum EConnectionProfileType {
  CLIENT,
  VENDOR,
}

export interface IProvider {
  id: string;
  type: EConnectionProfileType;
  name: string;
  ip: string;
  systemId: string;
  password: string;
  systemType: string;
  bindType: ESmppBindType;
  active: boolean;
  sourceTon: ESmppTypeOfNumber;
  sourceNpi: ESmppNumberingPlanIndicator;
  destinationTon: ESmppTypeOfNumber;
  destinationNpi: ESmppNumberingPlanIndicator;
  maximumConnections: number;
  tps: number;
  smppVersion: ESmppInterfaceVersion;
  isDeleted: boolean;
  profileId: string;
  routingPlanId: string;
  port: number;
  windowSize?: string | number;
  vpnTunnelId?: number | null;
  smppScheduleDeliveryTime?: string;
  isUp: boolean;
}

export const fetchProviders = async (
  params: Params<IProvider>
): Promise<IPageable<IProvider>> => {
  try {
    console.log("Fetching providers...");
    const response = await axiosInstance.get("/connection-profiles/vendors", {
       params: { ...params, search: params.search ?? "" },
    });
    return response.data;
  } catch (error: any) {
    console.error("Error fetching providers:", error.message || error);
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching the providers."
    );
  }
};

export async function fetchProvider(id: string): Promise<IProvider> {
  if (!id) {
    throw new Error("Provider ID is required.");
  }

  try {
    const response = await axiosInstance.get(
      `/connection-profiles/vendors/${id}`
    );
    return response.data;
  } catch (error: any) {
    // console.error("Error fetching provider:", error);

    const errorMessage =
      error.response?.data?.message ||
      (error.response?.status === 404
        ? "Provider not found."
        : "An error occurred while getting the provider.");

    throw new Error(errorMessage);
  }
}

export const addProviderApi = async (data: Partial<IProvider>) => {
  try {
    const response = await axiosInstance.post(
      `/connection-profiles/vendors`,
      data
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the provider."
    );
  }
};

export const editProviderApi = async (id: string, data: Partial<IProvider>) => {
  try {
    const response = await axiosInstance.put(
      `/connection-profiles/vendors/${id}`,
      data
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while updating the provider.`
    );
  }
};

export const getProviderById = async (id: string): Promise<IProvider> => {
  try {
    const response = await axiosInstance.get(
      `/connection-profiles/vendors/${id}`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while fetching the provider.`
    );
  }
};

export async function deleteProviderApi(id: string): Promise<void> {
  try {
    const response = await axiosInstance.delete(
      `/connection-profiles/vendors/${id}`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || "Failed to delete the VENDOR account."
    );
  }
}
export async function telnetApi(id: string): Promise<void> {
  try {
    const response = await axiosInstance.post(
      `/connection-profiles/vendors/${id}/telnet`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || "Failed to delete the VENDOR account."
    );
  }
}

export const useProvidersQuery = (
  params: Params<IProvider>
): UseQueryResult<IPageable<IProvider>, Error> => {
  const queryKey = ["providers", JSON.stringify(params)];
  return useQuery({
    queryKey,
    queryFn: () =>  fetchProviders(params),
  });
};

export const useProviderOptionQuery = (): UseQueryResult<Option[], Error> => {
  return useQuery({
    queryKey: ["providers"],
    queryFn: async () => {
      const providerResponse = await fetchProviders({
        size: config.MAX_PAGE_SIZE,
      });

      return providerResponse.content.map((provider: IProvider) => ({
        label: provider.name,
        value: provider.id,
      }));
    },
  });
};

export const getProviderOptions = (providerId: string) => ({
  queryKey: ["provider", providerId],
  queryFn: async (): Promise<string> => {
    try {
      const provider = await fetchProvider(providerId);
      return provider?.name || "-";
    } catch {
      return "-";
    }
  },
  select: (name: string) => name,
});